import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Dialog } from '@headlessui/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Button from '../buttons/Button';
import Divider from '../ui/Divider';
import { Complex, Field } from '../../types';
import { ROUTE_EDIT_ARENA_INFO } from '../../router/constants';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';
import { deleteField, getFields } from '../../api/fields';
import LoadingScreen from '../../pages/LoadingScreen';
import FieldCard from './FieldCard';
import AreYouSureModal from '../modals/AreYouSureModal';
import { toast } from 'react-toastify';
import ImageCarousel from '../carousel/ImageCarousel';

interface Props {
  complex: Complex;
  handleDeleteComplex: (id: number) => Promise<void>;
}

const ComplexSettingsCard = ({ complex, handleDeleteComplex }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<Field | null>(null);

  const [fetchingFields, setFetchingFields] = useState(false);
  const [fields, setFields] = useState<Field[]>([]);

  const navigate = useNavigate();

  const { t } = useTranslation();

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleConfirmDelete = useCallback(
    async (complexId: number) => {
      await handleDeleteComplex(complexId);
      closeModal();
    },
    [handleDeleteComplex]
  );

  const fetchFields = useCallback(async () => {
    if (complex.fieldsCount > 0) {
      setFetchingFields(true);

      const { data } = await getFields(complex.id, 1, 999);

      if (data) {
        setFields(data.fields);
      }

      setFetchingFields(false);
    }
  }, [complex]);

  const handleDeleteField = useCallback(
    async (complexId: number, fieldId: number) => {
      const { data, error } = await deleteField(complexId, fieldId);

      if (error) {
        toast(error.error.message, {
          toastId: 'delete-field',
        });
        return;
      }

      if (data) {
        await fetchFields();
      }

      setFieldToDelete(null);
    },
    [fetchFields]
  );

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  return (
    <div className="bg-[#222223] w-full max-w-[27rem] p-6 rounded-md">
      <div className="mb-6">
        <div className="flex flex-col justify-between h-80">
          <p className="text-lg text-mainWhite font-bold">{complex.name}</p>
          {complex.imageUrls.length > 0 ? (
            <img
              src={complex.imageUrls[0]}
              alt="complex"
              className="object-fill h-48 border border-mainBlack rounded-lg"
            />
          ) : (
            <div className="h-48"></div>
          )}
          <div>
            <p className="text-[#ACACAC] break-words">
              <span className="text-mainWhite">
                {t(COMMON_TRANSLATE_KEYS.CITY)}:
              </span>
              {` ${complex.city}`}
            </p>
            <p className="text-[#ACACAC] break-words">
              <span className="text-mainWhite">
                {t(COMMON_TRANSLATE_KEYS.ADDRESS)}:
              </span>
              {` ${complex.address}`}
            </p>
          </div>
        </div>
      </div>

      <Divider mb={4} mt={4} color="bg-[#4A4A4A]" px={4} />

      <div className="flex flex-col gap-4">
        <Button
          borderColor="border-mainBlue"
          border="border"
          textColor="text-mainBlue"
          onClick={() =>
            navigate(`/arena/${complex.id}/fields?name=${complex.name}`)
          }
        >
          {t(COMMON_TRANSLATE_KEYS.VIEW_FIELDS)}
        </Button>

        <Button
          backgroundColor="bg-functionalGreen"
          textColor="text-gray-800"
          onClick={() => navigate(`/arena/add-field/${complex.id}`)}
        >
          {t(COMMON_TRANSLATE_KEYS.ADD_FIELD)}
        </Button>

        {complex.fieldsCount > 0 && (
          <Button
            backgroundColor="bg-functionalRed"
            textColor="text-mainWhite"
            onClick={() => setIsFieldModalOpen(true)}
          >
            {t(COMMON_TRANSLATE_KEYS.DELETE_FIELD)}
          </Button>
        )}
      </div>

      <Divider mb={4} mt={4} color="bg-[#4A4A4A]" px={4} />

      <div className="flex flex-col gap-4">
        <Button
          textColor="text-mainBlue"
          borderColor="border-mainBlue"
          border="border"
          onClick={() => navigate(`${ROUTE_EDIT_ARENA_INFO}/${complex.id}`)}
        >
          {t(COMMON_TRANSLATE_KEYS.EDIT_COMPLEX)}
        </Button>

        <Button
          textColor="text-cancel"
          borderColor="border-cancel"
          border="border"
          onClick={() => openModal()}
        >
          {t(COMMON_TRANSLATE_KEYS.DELETE_COMPLEX)}
        </Button>
      </div>

      <AreYouSureModal
        isOpen={isOpen}
        closeModal={closeModal}
        title={`${t(COMMON_TRANSLATE_KEYS.DELETE_COMPLEX_CONFIRMATION_TEXT, {
          complex: complex.name,
        })}?`}
        additionalText={t(COMMON_TRANSLATE_KEYS.DELETE_COMPLEX_ADDITIONAL_TEXT)}
        onConfirm={async () => await handleConfirmDelete(complex.id)}
        onDecline={() => closeModal()}
      />

      <Dialog
        open={isFieldModalOpen}
        onClose={() => setIsFieldModalOpen(false)}
        className="fixed inset-0 flex items-center justify-center z-50"
      >
        <Dialog.Overlay className="fixed inset-0 bg-black  opacity-50" />
        <div className="bg-gray-900 w-[70%] h-[70%] max-h-[70%] overflow-hidden p-6 rounded-md absolute top-212 left-468">
          <Dialog.Title>
            <div className="flex flex-col">
              <div className="flex w-full justify-end text-mainWhite">
                <button onClick={() => setIsFieldModalOpen(false)}>X</button>
              </div>
              <h1 className="flex flex-row justify-start text-white text-xl font-bold mb-6 text-start">
                {t(COMMON_TRANSLATE_KEYS.FIELDS)}
              </h1>
            </div>
          </Dialog.Title>

          <Dialog.Description className="max-h-[100%] pb-20 overflow-auto">
            <div className="flex flex-row flex-wrap gap-1">
              {fetchingFields && <LoadingScreen />}
              {fields.map((f) => (
                <FieldCard
                  key={f.id}
                  complex={complex}
                  field={f}
                  title={f.name}
                  width="lg"
                >
                  <Button
                    backgroundColor="bg-functionalRed"
                    textColor="text-mainWhite"
                    onClick={() => setFieldToDelete(f)}
                  >
                    {t(COMMON_TRANSLATE_KEYS.DELETE)}
                  </Button>
                </FieldCard>
              ))}
            </div>
          </Dialog.Description>
        </div>

        {fieldToDelete && (
          <AreYouSureModal
            isOpen={fieldToDelete !== null}
            closeModal={() => setFieldToDelete(null)}
            title={`${t(COMMON_TRANSLATE_KEYS.DELETE_FIELD_CONFIRM, {
              field: fieldToDelete.name,
            })}?`}
            onConfirm={async () =>
              await handleDeleteField(complex.id, fieldToDelete.id)
            }
            onDecline={() => setFieldToDelete(null)}
            additionalText={t(COMMON_TRANSLATE_KEYS.DELETE_FIELD_ADDITIONAL_TEXT)}
          />
        )}
      </Dialog>
    </div>
  );
};

export default ComplexSettingsCard;
